import React, { useMemo, useState } from 'react';
import Section from './Section';
import { ShareModalView } from '../../components/share';
import { Dropdown } from '../../components/ui';
import { OrgMemberOption, RFQAccessList, RFQRole } from '../../types';
import { matchesQuery } from '../../hooks/useRfqSharing';

/**
 * /design pane for the Share modal. The panel body is rendered without the
 * modal shell so every state sits side by side on one page — the shell itself
 * is already covered by the Modal pane in the primitives gallery.
 *
 * The panes are live: type in them, arrow through the results, pick people
 * into chips, open the role dropdown. Nothing is wired to a server, so Share
 * and Remove do not move the access list.
 */

const ROSTER: OrgMemberOption[] = [
  { userId: 'u-owner', name: 'Dana Okafor', email: 'dana@northwind.example', existingRole: 'OWNER' },
  { userId: 'u-sam', name: 'Sam Reyes', email: 'sam@northwind.example', existingRole: 'EDITOR' },
  { userId: 'u-priya', name: 'Priya Nair', email: 'priya@northwind.example', existingRole: null },
  { userId: 'u-tom', name: 'Tom Whitfield', email: 'tom@northwind.example', existingRole: null },
  { userId: 'u-lea', name: null, email: 'lea@northwind.example', existingRole: null },
];

const OWNER = { userId: 'u-owner', name: 'Dana Okafor', email: 'dana@northwind.example', role: 'OWNER' as RFQRole };

const SHARED: RFQAccessList = {
  owner: OWNER,
  collaborators: [
    { userId: 'u-sam', name: 'Sam Reyes', email: 'sam@northwind.example', role: 'EDITOR', addedAt: '2026-08-21T09:12:00Z' },
  ],
};

const UNSHARED: RFQAccessList = { owner: OWNER, collaborators: [] };

interface PaneFixture {
  access: RFQAccessList;
  members?: OrgMemberOption[];
  initialQuery?: string;
  initialStaged?: string[];
  error?: string;
}

/** One live copy of the panel — each pane gets its own state. */
const SharePane: React.FC<PaneFixture> = ({
  access, members = ROSTER, initialQuery = '', initialStaged = [], error,
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [stagedIds, setStagedIds] = useState<string[]>(initialStaged);
  const [role, setRole] = useState<RFQRole>('EDITOR');

  const matches = useMemo(() => members.filter((m) => matchesQuery(m, query)), [members, query]);
  const staged = useMemo(
    () => stagedIds.map((id) => members.find((m) => m.userId === id)).filter((m): m is OrgMemberOption => Boolean(m)),
    [stagedIds, members],
  );

  return (
    <div className="w-full max-w-[620px] rounded-panel border border-line bg-surface p-s6">
      <ShareModalView
        access={access}
        members={members}
        matches={matches}
        staged={staged}
        onToggleStaged={(id) =>
          setStagedIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]))
        }
        onUnstage={(id) => setStagedIds((prev) => prev.filter((s) => s !== id))}
        onUnstageLast={() => setStagedIds((prev) => prev.slice(0, -1))}
        query={query}
        onQueryChange={setQuery}
        role={role}
        onRoleChange={setRole}
        onShare={() => undefined}
        onRemove={() => undefined}
        loading={false}
        sharing={false}
        removingUserId={null}
        error={error ?? null}
      />
    </div>
  );
};

const Pane: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex min-w-0 flex-col gap-s3">
    <p className="m-0 text-meta font-medium text-ink-muted">{label}</p>
    {children}
  </div>
);

const ShareGallery: React.FC = () => (
  <Section title="Share an RFQ (owner only)">
    <div className="flex flex-col gap-s7">
      <Pane label="Shared with one editor — nothing staged, so Share is off and says what would turn it on">
        <SharePane access={SHARED} />
      </Pane>

      <Pane label="Not shared yet — empty access list, and Robbie says what sharing would mean">
        <SharePane access={UNSHARED} members={ROSTER.filter((m) => m.existingRole !== 'EDITOR')} />
      </Pane>

      <Pane label="Two people staged as chips — one Share grants both, at the chosen role">
        <SharePane access={SHARED} initialStaged={['u-priya', 'u-lea']} />
      </Pane>

      <Pane label="Searching — teammates who already have access stay visible, marked, and cannot be picked">
        <SharePane access={SHARED} initialQuery="a" initialStaged={['u-tom']} />
      </Pane>

      <Pane label="Nothing matches — say so, don't show an empty box">
        <SharePane access={SHARED} initialQuery="zx" />
      </Pane>

      <Pane label="A refusal, in Robbie's voice">
        <SharePane
          access={SHARED}
          error="Only Dana Okafor can change who has access to this RFQ, so I left the list as it was. Ask Dana Okafor to make the change."
        />
      </Pane>

      <Pane label="Role dropdown on its own — click to open, ↑/↓ to move, Enter to choose">
        <div className="w-fit rounded-card border border-line-divider p-s5">
          <Dropdown
            options={[{ value: 'EDITOR', label: 'Editor', description: 'Can change everything except who has access' }]}
            value="EDITOR"
            onChange={() => undefined}
            ariaLabel="Role"
            className="w-[132px]"
          />
        </div>
      </Pane>
    </div>
  </Section>
);

export default ShareGallery;
